import React, { useState, useCallback } from 'react';
import { useObserver } from 'mobx-react-lite';
import styled, { keyframes } from 'styled-components';
import dayjs from 'dayjs';
import { EuiIcon, EuiButton, EuiToolTip } from '@elastic/eui';

import uptimeStore from '../../../../stores/uptime.store';
import uptimeChecksStore from '../../../../stores/uptimeChecks.store';
import alarmsStore from '../../../../stores/alarms.store';
import utilsService from '../../../../servises/utils.service';
import { useHistory } from 'react-router-dom';

import MiniPinyWidget from './serverWidgets/MiniPinyWidget';
import AlarmsWidget from './serverWidgets/AlarmsWidget';
import BaseChart from './chart/BaseChart';
import { addToast } from '../../../components/toasts/Toast';

// Keyframes
const pulseGlow = keyframes`
  0% { transform: scale(0.95); opacity: 0.6; box-shadow: 0 0 0 0 currentColor; }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 8px 3px currentColor; }
  100% { transform: scale(0.95); opacity: 0.6; box-shadow: 0 0 0 0 currentColor; }
`;

// Styled Components
const MainPageWrapper = styled.div`
  padding-bottom: 30px;
  background-color: rgb(18, 19, 23);
  min-height: 100vh;
`;

const UptimeHeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: linear-gradient(180deg, rgba(35, 37, 47, 0.4) 0%, rgba(24, 25, 30, 0) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  margin-bottom: 16px;

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 10px;
  }
`;

const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: rgb(0, 128, 213);
  }
`;

const HeaderMain = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const HeaderTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-all;

  @media (max-width: 600px) {
    font-size: 18px;
    flex-wrap: wrap;
  }
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.up {
    background: rgba(80, 188, 94, 0.1);
    color: #50bc5e;
    border: 1px solid rgba(80, 188, 94, 0.15);
  }
  
  &.down {
    background: rgba(248, 107, 99, 0.1);
    color: #F86B63;
    border: 1px solid rgba(248, 107, 99, 0.15);
  }
  
  &.disabled {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
  
  &.unknown {
    background: rgba(249, 190, 73, 0.1);
    color: #f9be49;
    border: 1px solid rgba(249, 190, 73, 0.15);
  }
`;

const StatusDot = styled.span`
  position: relative;
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: currentColor;
  animation: ${pulseGlow} 2s infinite ease-in-out;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    
    button, a {
      width: 100%;
    }
  }
`;

// Overview Grid Styling
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 12px;
  margin: 10px 20px 20px 20px;
  width: calc(100% - 40px);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    margin: 10px 16px 16px 16px;
    width: calc(100% - 32px);
  }
`;

const OverviewCard = styled.div`
  background: linear-gradient(135deg, rgb(35, 37, 47) 0%, rgb(24, 25, 30) 100%);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.35);
    border-color: ${props => props.accentColor || 'rgba(0, 128, 213, 0.3)'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${props => props.accentColor || 'rgb(0, 128, 213)'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;

  h3 {
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  gap: 8px;

  span.val {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-family: Consolas, monospace;
    background: rgba(255, 255, 255, 0.04);
    padding: 2px 6px;
    border-radius: 4px;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    @media (min-width: 480px) {
      max-width: 200px;
    }
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.2s;

  &:hover {
    color: rgb(0, 128, 213);
  }
`;

const LinkButton = styled.a`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.2s;
  cursor: pointer;

  &:hover {
    color: rgb(0, 128, 213);
  }
`;

// Grid Layout
const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 16px;
  padding: 0 20px;
  margin-bottom: 30px;
  min-width: 0;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

const LayoutMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const LayoutSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const SectionCard = styled.section`
  background-color: rgb(29, 30, 36);
  border-radius: 6px;
  padding: 18px;
  box-shadow: rgb(0 0 0 / 28%) 0px 0.9px 4px, rgb(0 0 0 / 21%) 0px 2.6px 8px;
  border: 1px solid rgba(255, 255, 255, 0.03);
  min-width: 0;
  
  h3 {
    font-size: 15px;
    font-weight: 700;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    padding-bottom: 8px;
  }
`;

// Main Component Wrapper
const UptimeItemWrapper = props => {
    const { uptimeId } = props;

    return useObserver(() => (
        <UptimeItem
            uptimeId={ uptimeId }
            uptime={ uptimeStore.uptimeListObj[uptimeId] }
            alarms={ alarmsStore.alarmsByServerObj[uptimeId] }
            checks={ uptimeChecksStore.uptimeChecksList }
        />
    ));
};

export default UptimeItemWrapper;

// Main Uptime Page Detail Component
const UptimeItem = props => {
    const history = useHistory();
    const { uptimeId, uptime, checks, alarms } = props;
    
    const [isSettingStatus, setIsSettingStatus] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!uptime) {
        return null;
    }

    // Set page title
    document.title = `${ uptime.name } - SyAgent`;

    // Copy URL handler
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Pause / Enable monitor status handler
    const uptimeStatusHandle = async () => {
        if (isSettingStatus) return;
        setIsSettingStatus(true);
        try {
            await uptimeStore.updateUptime(uptime._id, {
                isEnabled: !uptime.isEnabled
            });
        } catch (error) {
            addToast(error.message || 'Failed to change monitor status', 'error');
        } finally {
            setIsSettingStatus(false);
        }
    };

    const navigateToEditMonitor = () => {
        history.push(`/uptime/${ uptime._id }/edit`);
    };

    const navigateBack = () => {
        history.push('/servers');
    };

    // Derive class & status pill
    let statusClass = 'unknown';
    let statusText = 'Unknown';
    let accentColor = '#f9be49';

    if (uptime.classes === 'normal') {
        statusClass = 'up';
        statusText = 'Up';
        accentColor = '#50bc5e';
    } else if (uptime.classes === 'old-data') {
        statusClass = 'down';
        statusText = 'Down';
        accentColor = '#F86B63';
    } else if (uptime.classes === 'disabled') {
        statusClass = 'disabled';
        statusText = 'Paused';
        accentColor = 'rgba(255, 255, 255, 0.4)';
    } else if (uptime.classes === 'no-data') {
        statusClass = 'unknown';
        statusText = 'No data';
        accentColor = '#f9be49';
    }

    // Fallback to inline checks from uptime object if store list is empty/fetching
    const activeChecks = (checks && checks.length > 0) ? checks : (uptime.uptimechecks || []);

    // Dynamic SLA and response time calculations
    const metrics = calculateMetrics(activeChecks, uptime);

    return (
        <MainPageWrapper className="mini-pini">
            {/* Unified Hero Header */}
            <UptimeHeaderContainer>
                <BackLink onClick={ navigateBack }>
                    <EuiIcon type="arrowLeft" size="m" />
                    Back to Monitors
                </BackLink>
                <HeaderMain>
                    <HeaderTitleSection>
                        <Title>
                            <EuiIcon type="uptimeApp" size="l" style={{ color: accentColor }} />
                            { uptime.name }
                        </Title>
                        <StatusPill className={ statusClass }>
                            <StatusDot />
                            { statusText }
                        </StatusPill>
                    </HeaderTitleSection>
                    <HeaderActions>
                        <EuiButton 
                            onClick={ uptimeStatusHandle }
                            size="s"
                            isLoading={ isSettingStatus }
                            iconType={ uptime.isEnabled ? 'pause' : 'play' }
                            color={ uptime.isEnabled ? 'warning' : 'success' }
                        >
                            { uptime.isEnabled ? 'Pause' : 'Enable' }
                        </EuiButton>
                        <EuiButton 
                            onClick={ navigateToEditMonitor }
                            size="s"
                            iconType="documentEdit"
                            color="text"
                        >
                            Edit
                        </EuiButton>
                    </HeaderActions>
                </HeaderMain>
            </UptimeHeaderContainer>

            {/* Overview Stat Cards Grid */}
            <OverviewGrid>
                {/* Card 1: Status & Connectivity */}
                <OverviewCard accentColor={ accentColor }>
                    <CardHeader>
                        <h3><EuiIcon type="heart" size="m"/> Health Status</h3>
                        <span style={{ fontSize: '10px', color: accentColor, fontWeight: 700, textTransform: 'uppercase' }}>
                            { statusText }
                        </span>
                    </CardHeader>
                    <CardContent>
                        <InfoRow>
                            <span>Connectivity:</span>
                            <span className="val" style={{ color: uptime.classes === 'normal' ? '#50bc5e' : '#F86B63' }}>
                                { uptime.classes === 'normal' ? 'Healthy' : uptime.classes === 'disabled' ? 'Inactive' : 'Failing' }
                            </span>
                        </InfoRow>
                        <InfoRow>
                            <span>Last Run:</span>
                            <span className="val">{ metrics.lastChecked }</span>
                        </InfoRow>
                        <InfoRow>
                            <span>Performance:</span>
                            <span className="val">{ metrics.okCount }/{ metrics.totalChecks } OK</span>
                        </InfoRow>
                    </CardContent>
                </OverviewCard>

                {/* Card 2: Target Configuration */}
                <OverviewCard accentColor="rgb(0, 128, 213)">
                    <CardHeader>
                        <h3><EuiIcon type="globe" size="m"/> Target Endpoint</h3>
                        <EuiIcon type="subnetwork" size="m" style={{ opacity: 0.6 }}/>
                    </CardHeader>
                    <CardContent>
                        <InfoRow>
                            <span>Protocol:</span>
                            <span className="val" style={{ textTransform: 'uppercase' }}>{ uptime.type }</span>
                        </InfoRow>
                        <InfoRow>
                            <span>Target URL:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="val" title={ uptime.url } style={{ maxWidth: '100px' }}>{ uptime.url }</span>
                                <CopyButton onClick={ () => handleCopy(uptime.url) } title="Copy Endpoint">
                                    <EuiIcon type={ copied ? "check" : "copy" } size="s"/>
                                </CopyButton>
                                <LinkButton href={ uptime.url.startsWith('http') ? uptime.url : `http://${uptime.url}` } target="_blank" rel="noreferrer" title="Open Target">
                                    <EuiIcon type="exportAction" size="s"/>
                                </LinkButton>
                            </div>
                        </InfoRow>
                    </CardContent>
                </OverviewCard>

                {/* Card 3: Check Interval & Schedule */}
                <OverviewCard accentColor="#f9be49">
                    <CardHeader>
                        <h3><EuiIcon type="clock" size="m"/> Schedule</h3>
                        <EuiIcon type="compute" size="m" style={{ opacity: 0.6 }}/>
                    </CardHeader>
                    <CardContent>
                        <InfoRow>
                            <span>Frequency:</span>
                            <span className="val">{ utilsService.calculateEveryTime(uptime) || 'N/A' }</span>
                        </InfoRow>
                        <InfoRow>
                            <span>Next Check:</span>
                            <span className="val" style={{ color: '#f9be49' }}>
                                { uptime.isEnabled ? dayjs(uptime.scheduleTo).fromNow() : 'Paused' }
                            </span>
                        </InfoRow>
                        <InfoRow>
                            <span>Created:</span>
                            <span className="val">{ dayjs(uptime.createdAt).format('DD MMM YYYY') }</span>
                        </InfoRow>
                    </CardContent>
                </OverviewCard>

                {/* Card 4: SLA & Latency Metrics */}
                <OverviewCard accentColor="#8884d8">
                    <CardHeader>
                        <h3><EuiIcon type="dashboardApp" size="m"/> Latency & SLA</h3>
                        <span style={{ fontSize: '11px', color: '#8884d8', fontWeight: 600 }}>Europe</span>
                    </CardHeader>
                    <CardContent>
                        <InfoRow>
                            <span>Uptime SLA:</span>
                            <span className="val" style={{ color: '#50bc5e', fontWeight: 700 }}>{ metrics.sla }</span>
                        </InfoRow>
                        <InfoRow>
                            <span>Avg Latency:</span>
                            <span className="val" style={{ color: '#8884d8' }}>{ metrics.avgLatency }</span>
                        </InfoRow>
                        <InfoRow>
                            <span>Min / Max:</span>
                            <span className="val">{ metrics.minLatency } / { metrics.maxLatency }</span>
                        </InfoRow>
                    </CardContent>
                </OverviewCard>
            </OverviewGrid>

            {/* Layout Main / Sidebar sections */}
            <LayoutGrid>
                <LayoutMain>
                    {/* Interactive Status Timeline */}
                    <SectionCard>
                        <h3><EuiIcon type="uptimeApp" size="m" /> Uptime check history</h3>
                        <MiniPinyWidget
                            uptime={ uptime }
                            uptimeChecks={ activeChecks }
                            isBig={ true }
                        />
                    </SectionCard>

                    {/* Latency History Chart */}
                    <BaseChart
                        icon="timeslider"
                        title="Response time (last 100 runs)"
                        isLoading={ false }
                        hideTime={ true }
                        data={ generateChartData(activeChecks) }
                    />
                </LayoutMain>

                <LayoutSidebar>
                    {/* Alarms Widget */}
                    <SectionCard style={{ padding: '14px' }}>
                        <AlarmsWidget
                            alarms={ alarms }
                            server={ uptime }
                            hideTitle={ false }
                        />
                    </SectionCard>
                </LayoutSidebar>
            </LayoutGrid>
        </MainPageWrapper>
    );
};

// Calculations for Uptime, SLA and response latency
const calculateMetrics = (checks, uptime) => {
    let sla = '100.0%';
    let avgLatency = '0ms';
    let minLatency = '0ms';
    let maxLatency = '0ms';
    let lastChecked = 'Never';
    let okCount = 0;
    
    // Filter out pending checks that don't have response latency
    const actualChecks = Array.isArray(checks) 
        ? checks.filter(c => c.createdAt && c.responseTime !== undefined && c.isValid !== undefined)
        : [];
        
    const total = actualChecks.length;
    
    if (total > 0) {
        const successChecks = actualChecks.filter(c => c.isValid);
        okCount = successChecks.length;
        sla = ((okCount / total) * 100).toFixed(1) + '%';
        
        let sum = 0;
        let min = Infinity;
        let max = -Infinity;
        
        actualChecks.forEach(c => {
            const r = c.responseTime || 0;
            sum += r;
            if (r < min) min = r;
            if (r > max) max = r;
        });
        
        avgLatency = Math.round(sum / total) + 'ms';
        minLatency = min === Infinity ? '0ms' : min + 'ms';
        maxLatency = max === -Infinity ? '0ms' : max + 'ms';
        
        // Find most recent check (last in array since store reversed them to chronological)
        const lastCheck = actualChecks[actualChecks.length - 1];
        if (lastCheck) {
            lastChecked = dayjs(lastCheck.createdAt).format('HH:mm:ss DD MMM');
        }
    }
    
    return {
        sla,
        avgLatency,
        minLatency,
        maxLatency,
        lastChecked,
        okCount,
        totalChecks: total
    };
};

// Map database check objects to Recharts compatible objects
const generateChartData = checks => {
    const result = {
        ext: 'ms',
        key: 'rsp',
        data: []
    };

    if (Array.isArray(checks)) {
        for (let i = 0; i < checks.length; i++) {
            const check = checks[i];
            if (check.responseTime !== undefined) {
                const date = utilsService.timeMe(check.createdAt);
                result.data.push({
                    name: date,
                    rsp: check.responseTime
                });
            }
        }
    }
    return result;
};
